/**
 * @file WmsSkuSelectDialog.tsx
 * @description Одиночный выбор фасовки для панели складского документа
 * (phase19.24 этап 1, шаг 1; решение пользователя 2026-09-20).
 *
 * Кнопка «Подбор» в панели добавляет ОДНУ строку за раз, поэтому здесь нет ни
 * галочек, ни счётчика «Выбрано: 3», ни футера: клик по строке выбирает
 * фасовку и закрывает шторку. Массовый `WmsSkuPickerDialog` с галочками
 * остался только у кнопки «Добавить несколько» в заголовке «Позиции».
 *
 * Источник — складской пикер `GET /admin/wms/skus` (право SHOP_WMS_VIEW, без
 * цен): заказной `/admin/orders/skus` кладовщику недоступен по правам.
 *
 * Уже добавленные фасовки видны серыми: клик уводит к строке документа
 * (`onGoToExisting`), а не заводит дубль.
 *
 * Фокус после закрытия задаёт `usePickerFinalFocus`: выбрали строку — курсор
 * уходит в следующее поле панели, закрыли без выбора — остаётся там, куда его
 * поставил экран. Без этого base-ui возвращал фокус на триггер «Подбор»,
 * которого после выбора уже нет, и курсор падал в `body`.
 */

import { Loader2, Package, Search } from "lucide-react";
import { useEffect, useState, type RefObject } from "react";

import { SkuSelectRow } from "./SkuSelectRow";
import type { WmsPickerSku } from "./WmsSkuPickerDialog";
import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { usePickerFinalFocus } from "../ui/usePickerFinalFocus";
import { formatPickerSkuName, wmsSkusApi } from "../../lib/api-wms";
import { formatSalePackage } from "../../lib/qty-input";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Выбрана фасовка — панель подставляет её в форму и закрывает шторку. */
  onSelect: (sku: WmsPickerSku) => void;
  /** Уже добавленные skuId — показываем серыми. */
  alreadyAddedIds?: string[];
  /** Перейти к существующей строке документа по skuId (сама фасовка — вторым). */
  onGoToExisting?: (skuId: string, sku: WmsPickerSku) => void;
  /** Поле панели, куда уходит курсор после выбора строки («Количество»). */
  nextFieldRef?: RefObject<HTMLElement | null>;
}

/** «50 г» — объём фасовки по реестру единиц (TD-53), иначе пусто. */
function packageText(sku: WmsPickerSku): string | undefined {
  return sku.saleQuantity && sku.saleUnit
    ? formatSalePackage(sku.saleQuantity, sku.saleUnit)
    : undefined;
}

export function WmsSkuSelectDialog({
  open,
  onClose,
  onSelect,
  alreadyAddedIds = [],
  onGoToExisting,
  nextFieldRef,
}: Props) {
  const focus = usePickerFinalFocus(nextFieldRef);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<WmsPickerSku[]>([]);
  /** Всего под фильтром — показываем, когда список обрезан лимитом. */
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    // Debounce: поле ловит и ввод руками, и «пачку» со сканера.
    const timer = setTimeout(() => {
      wmsSkusApi
        .search({ search: search.trim() || undefined, limit: 100 })
        .then((page) => {
          if (cancelled) return;
          setItems(page.items);
          setTotal(page.total);
          setError(null);
        })
        .catch(() => {
          if (cancelled) return;
          setItems([]);
          setTotal(0);
          setError("Не удалось загрузить список фасовок");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, open]);

  // Сброс поиска при закрытии: следующий вызов открывается чистым.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <SheetContent
        side="right"
        finalFocus={focus.finalFocus}
        className="flex w-full flex-col gap-0 p-0 sm:max-w-lg"
      >
        <SheetHeader className="shrink-0 border-b border-on-surface/8 px-4 py-3">
          <SheetTitle>Выбрать фасовку</SheetTitle>
        </SheetHeader>

        <div className="shrink-0 border-b border-on-surface/8 px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Название товара или артикул"
              className="min-h-[56px] pl-9 text-base"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-6 animate-spin text-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-sm text-on-surface-variant">
              <Package className="size-8" />
              {error ?? "Фасовок не найдено"}
            </div>
          ) : (
            <>
              <ul className="flex flex-col gap-1">
                {items.map((sku) => (
                  <SkuSelectRow
                    key={sku.id}
                    title={formatPickerSkuName(sku)}
                    code={sku.code}
                    note={packageText(sku)}
                    added={alreadyAddedIds.includes(sku.id)}
                    onSelect={() => {
                      focus.markPicked();
                      onSelect(sku);
                    }}
                    onGoToExisting={
                      onGoToExisting ? () => onGoToExisting(sku.id, sku) : undefined
                    }
                  />
                ))}
              </ul>
              {total > items.length ? (
                <p className="px-4 py-3 text-center text-xs text-on-surface-variant">
                  Показаны {items.length} из {total} — уточните поиск.
                </p>
              ) : null}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
